import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FileText, Search, Printer, Key, Trash2, Loader2, X, ChevronDown, Package } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ALL = "__all__";

function fmtDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

export default function Worksheets() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [q, setQ] = useState("");
  const [studentId, setStudentId] = useState(ALL);
  const [subject, setSubject] = useState(ALL);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [packBusy, setPackBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    setError("");
    try {
      const params = new URLSearchParams();
      if (q.trim()) params.set("q", q.trim());
      if (studentId !== ALL) params.set("student_id", studentId);
      if (subject !== ALL) params.set("subject", subject);
      if (from) params.set("from_date", from);
      if (to) params.set("to_date", to);
      const { data } = await api.get(`/worksheets${params.toString() ? "?" + params.toString() : ""}`);
      setData(data);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [studentId, subject, from, to]);

  // Debounce the free-text search
  useEffect(() => {
    const t = setTimeout(() => { load(); }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q]);

  const clearAll = () => {
    setQ(""); setStudentId(ALL); setSubject(ALL); setFrom(""); setTo("");
  };

  const openPdf = (id, withAnswers) => {
    window.open(`${BACKEND_URL}/api/worksheets/${id}/print${withAnswers ? "?with_answers=true" : ""}`, "_blank", "noopener");
  };

  const remove = async (w) => {
    if (!window.confirm(`Delete "${w.title}"? This can't be undone.`)) return;
    try {
      await api.delete(`/worksheets/${w.id}`);
      toast.success("Worksheet deleted");
      setSelected((s) => { const n = new Set(s); n.delete(w.id); return n; });
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const toggleOne = (id) => {
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };
  const toggleAll = () => {
    if (selected.size === list.length && list.length > 0) {
      setSelected(new Set());
    } else {
      setSelected(new Set(list.map((w) => w.id)));
    }
  };

  const printSelected = async (withAnswers) => {
    if (selected.size === 0) return;
    setPackBusy(true);
    try {
      const res = await api.post(
        "/worksheets/print-pack",
        { worksheet_ids: Array.from(selected), with_answers: withAnswers },
        { responseType: "blob" }
      );
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `print-pack-${new Date().toISOString().slice(0, 10)}${withAnswers ? "-with-answers" : ""}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Print pack downloaded (${selected.size} worksheet${selected.size === 1 ? "" : "s"})`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setPackBusy(false);
    }
  };

  const hasFilters = q || studentId !== ALL || subject !== ALL || from || to;
  const list = data?.worksheets || [];
  const filters = data?.filters || { subjects: [], students: [] };

  return (
    <div className="space-y-8" data-testid="worksheets-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-indigo-600">Premium tool</div>
          <h1 className="mt-2 font-display text-3xl sm:text-4xl font-bold tracking-tight">Worksheet Library</h1>
          <p className="mt-2 text-stone-500 text-sm">Every worksheet generated for your students — searchable, filterable, reprintable anytime.</p>
        </div>
      </div>

      {/* Filter bar */}
      <div className="grid gap-3 md:grid-cols-[1fr,180px,180px,140px,140px,auto]" data-testid="worksheets-filter-bar">
        <div className="relative">
          <Search className="w-4 h-4 text-stone-400 absolute left-3 top-3.5 pointer-events-none" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search titles…"
            className="h-11 pl-9 rounded-xl"
            data-testid="worksheets-search-input"
          />
        </div>
        <Select value={studentId} onValueChange={setStudentId}>
          <SelectTrigger className="h-11 rounded-xl" data-testid="worksheets-student-filter">
            <SelectValue placeholder="Student" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All students</SelectItem>
            {filters.students.map((s) => (
              <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={subject} onValueChange={setSubject}>
          <SelectTrigger className="h-11 rounded-xl" data-testid="worksheets-subject-filter">
            <SelectValue placeholder="Subject" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All subjects</SelectItem>
            {filters.subjects.map((s) => (
              <SelectItem key={s} value={s}>{s}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input
          type="date"
          value={from}
          onChange={(e) => setFrom(e.target.value)}
          className="h-11 rounded-xl"
          data-testid="worksheets-from-input"
        />
        <Input
          type="date"
          value={to}
          onChange={(e) => setTo(e.target.value)}
          className="h-11 rounded-xl"
          data-testid="worksheets-to-input"
        />
        <Button
          variant="ghost"
          onClick={clearAll}
          disabled={!hasFilters}
          className="rounded-full text-stone-600 hover:text-stone-900"
          data-testid="worksheets-clear-filters"
        >
          <X className="w-4 h-4 mr-1" /> Clear
        </Button>
      </div>

      {/* Result count + loading + bulk actions */}
      <div className="flex items-center justify-between gap-4 flex-wrap text-xs text-stone-500" data-testid="worksheets-count">
        <span>{busy ? "Searching…" : `${list.length} worksheet${list.length === 1 ? "" : "s"}`}</span>
        <div className="flex items-center gap-2 flex-wrap">
          {selected.size > 0 && (
            <>
              <span className="text-stone-700 font-medium" data-testid="selected-count">
                {selected.size} selected
              </span>
              <Button
                size="sm"
                onClick={() => printSelected(false)}
                disabled={packBusy}
                className="rounded-full bg-stone-900 hover:bg-stone-800 text-white h-8"
                data-testid="print-selected-btn"
              >
                {packBusy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Package className="w-3.5 h-3.5 mr-1.5" />}
                Print pack ({selected.size})
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => printSelected(true)}
                disabled={packBusy}
                className="rounded-full border-indigo-300 text-indigo-700 hover:bg-indigo-50 h-8"
                data-testid="print-selected-with-answers-btn"
              >
                <Key className="w-3.5 h-3.5 mr-1.5" /> With answer keys
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setSelected(new Set())}
                className="rounded-full text-stone-500 h-8"
                data-testid="clear-selection-btn"
              >
                Clear selection
              </Button>
            </>
          )}
          {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
        </div>
      </div>

      {/* Table / empty state */}
      {error && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2" data-testid="worksheets-error">
          {error}
        </div>
      )}
      {!error && list.length === 0 && !busy && (
        <div className="rounded-3xl border border-dashed border-stone-200 bg-white p-12 text-center" data-testid="worksheets-empty">
          <div className="mx-auto grid place-items-center w-14 h-14 rounded-2xl bg-indigo-50 text-indigo-700">
            <FileText className="w-6 h-6" />
          </div>
          <div className="mt-4 font-display text-xl font-semibold">No worksheets yet</div>
          <p className="mt-2 text-stone-500 text-sm max-w-md mx-auto">
            Open any lesson in the Schedule tab and generate a printable worksheet — every one you create will land here for easy reprinting.
          </p>
          <Link to="/dashboard/schedule" className="mt-6 inline-block">
            <Button className="rounded-full bg-indigo-500 hover:bg-indigo-600 text-white">Go to Schedule</Button>
          </Link>
        </div>
      )}

      {list.length > 0 && (
        <div className="rounded-3xl border border-stone-200 bg-white overflow-hidden shadow-sm" data-testid="worksheets-table">
          <table className="w-full text-sm">
            <thead className="bg-stone-50">
              <tr className="text-left text-[10px] uppercase tracking-[0.15em] text-stone-500 font-semibold">
                <th className="py-3 pl-4 pr-2 w-10">
                  <Checkbox
                    checked={selected.size === list.length && list.length > 0}
                    onCheckedChange={toggleAll}
                    data-testid="select-all-checkbox"
                    aria-label="Select all worksheets"
                  />
                </th>
                <th className="py-3 px-4">Title</th>
                <th className="py-3 px-4">Student</th>
                <th className="py-3 px-4">Subject</th>
                <th className="py-3 px-4">Difficulty</th>
                <th className="py-3 px-4">Created</th>
                <th className="py-3 px-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100">
              {list.map((w) => (
                <tr key={w.id} className={`hover:bg-stone-50 transition-colors ${selected.has(w.id) ? "bg-indigo-50/40" : ""}`} data-testid={`worksheet-row-${w.id}`}>
                  <td className="py-3 pl-4 pr-2">
                    <Checkbox
                      checked={selected.has(w.id)}
                      onCheckedChange={() => toggleOne(w.id)}
                      data-testid={`select-worksheet-${w.id}`}
                      aria-label={`Select ${w.title}`}
                    />
                  </td>
                  <td className="py-3 px-4">
                    <div className="font-medium text-stone-900">{w.title}</div>
                    <div className="text-xs text-stone-500 mt-0.5">{w.question_count} question{w.question_count === 1 ? "" : "s"} · Grade {w.grade || "—"}</div>
                  </td>
                  <td className="py-3 px-4 text-stone-700">
                    {w.student_id ? (
                      <Link to={`/dashboard/students/${w.student_id}`} className="hover:text-indigo-700 hover:underline">
                        {w.student_name || "Student"}
                      </Link>
                    ) : (
                      <span>{w.student_name || "—"}</span>
                    )}
                  </td>
                  <td className="py-3 px-4">
                    <span className="inline-flex items-center rounded-full bg-stone-100 text-stone-700 px-2.5 py-0.5 text-xs font-semibold">{w.subject || "—"}</span>
                  </td>
                  <td className="py-3 px-4 text-stone-600 text-xs">{w.difficulty || "—"}</td>
                  <td className="py-3 px-4 text-stone-500 text-xs whitespace-nowrap">{fmtDate(w.created_at)}</td>
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-1 justify-end">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => openPdf(w.id, false)}
                        className="rounded-full text-stone-600 hover:text-stone-900 hover:bg-stone-100 h-8 px-3"
                        data-testid={`worksheet-print-${w.id}`}
                        title="Print worksheet"
                      >
                        <Printer className="w-3.5 h-3.5 mr-1" /> Print
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => openPdf(w.id, true)}
                        className="rounded-full text-indigo-700 hover:bg-indigo-50 h-8 px-3"
                        data-testid={`worksheet-key-${w.id}`}
                        title="Print answer key"
                      >
                        <Key className="w-3.5 h-3.5 mr-1" /> Key
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => remove(w)}
                        className="rounded-full text-stone-400 hover:text-red-600 hover:bg-red-50 h-8 w-8 p-0"
                        data-testid={`worksheet-delete-${w.id}`}
                        title="Delete worksheet"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
